import { useState } from 'react';
import {
  createServiceRequestByGroup,
  deleteServiceRequest,
  deleteServiceRequestFromGroup,
  deleteServiceRequestGroup,
  fetchServiceRequestActivityById,
  getServiceRequestById,
  saveScheduledAppointment,
  saveStatus,
  updateBulkStatus,
  updateServiceRequestOneYearWarrantyIssueStatus,
} from 'src/services/client';
import { toast } from 'react-toastify';
import { IServiceRequestDetail } from 'src/services/api/ServiceRequest/apiContracts';

interface ApiResponseWithData<TData = any> {
  statusCode: number;
  message: string;
  data: TData;
  warnings: string[] | null;
}

const ACTIVITY_LOG_WRITE_FAILED = 'activity_log_write_failed';

interface UseServiceRequestAPIState {
  serviceRequest: IServiceRequestDetail;
  activity: any[];
  loading: boolean;
  error: string | null;
  oneYearWarrantyIssues: any;
  suiteCounts: any;
}

export interface UseServiceRequestAPIReturn {
  serviceRequest: any;
  activity: any[];
  loading: boolean;
  error: string | null;
  oneYearWarrantyIssues: any;
  fetchServiceRequestById: (id: string) => Promise<any>;
  fetchServiceRequestActivity: (id: string) => Promise<void>;
  updateStatus: (statusData: any, serviceRequestId: string) => Promise<boolean>;
  saveScheduledAppointmentFromAPI: (
    formData: any,
    serviceRequestId: string
  ) => Promise<void>;
  deleteServiceRequestFromAPI: (serviceRequestId: string) => Promise<boolean>;
  updateServiceRequestOneYearWarrantyIssueStatusFromAPI: (
    data: any
  ) => Promise<void>;
  clearServiceRequestState: () => void;

  bulkUpdateStatus: (payload: any) => Promise<boolean>;
  groupServiceRequests: (payload: any) => Promise<boolean>;
  removeServiceRequestFromGroup: (
    serviceRequestId: string,
    userId: string
  ) => Promise<boolean>;
  deleteServiceRequestGroupById: (
    groupId: string,
    userId: string
  ) => Promise<boolean>;
}

export const useServiceRequestAPI = (
  token?: string
): UseServiceRequestAPIReturn => {
  const [state, setState] = useState<UseServiceRequestAPIState>({
    serviceRequest: null,
    activity: [],
    loading: false,
    error: null,
    oneYearWarrantyIssues: null,
    suiteCounts: null,
  });

  const setLoading = (loading: boolean) => {
    setState((prev: UseServiceRequestAPIState) => ({ ...prev, loading }));
  };

  const setError = (error: string | null) => {
    setState((prev: UseServiceRequestAPIState) => ({ ...prev, error }));
  };

  const fetchServiceRequestById = async (
    id: string
  ): Promise<IServiceRequestDetail | undefined> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await getServiceRequestById(id, token);

      if (response.ok || response.status === 200) {
        const data: IServiceRequestDetail = await response.json();

        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          serviceRequest: data,
          loading: false,
        }));

        return data;
      }

      throw new Error('Failed to fetch service request');
    } catch (error: any) {
      console.error('Error fetching service request:', error);
      setError(error.message || 'Failed to fetch service request');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceRequestActivity = async (id: string): Promise<void> => {
    try {
      const response: any = await fetchServiceRequestActivityById(id, token);

      if (response.ok || response.status === 200) {
        const activityData = await response.json();

        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          activity: activityData || [],
          serviceRequest: prev.serviceRequest
            ? { ...prev.serviceRequest, activity: activityData || [] }
            : null,
        }));
      }
    } catch (error: any) {
      console.error('Error fetching service request activity:', error);
    }
  };

  const updateStatus = async (
    statusData: any,
    serviceRequestId: string
  ): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await saveStatus(
        serviceRequestId,
        statusData,
        token
      );

      if (response.ok || response.status === 200) {
        const responseData: ApiResponseWithData = await response.json();

        toast.success('ⓘ Service request status has been updated.', {
          autoClose: 10000,
          position: 'top-center',
        });

        const activityLogFailed = responseData.warnings?.includes(
          ACTIVITY_LOG_WRITE_FAILED
        );

        if (activityLogFailed) {
          toast.warning(
            '⚠ Status changed, but the activity log entry could not be recorded. The change may not appear in the activity feed.',
            { autoClose: 12000, position: 'top-center' }
          );
        }

        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          serviceRequest: prev.serviceRequest
            ? { ...prev.serviceRequest, ...responseData.data }
            : null,
        }));

        await fetchServiceRequestActivity(serviceRequestId);
        return true;
      }

      throw new Error('Failed to update status');
    } catch (error: any) {
      console.error('Error updating status:', error);
      setError(error.message || 'Failed to update status');

      toast.error(
        '🚫 Something went wrong with changing the status of the service request, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const saveScheduledAppointmentFromAPI = async (
    formData: any,
    serviceRequestId: string
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await saveScheduledAppointment({
        ...formData,
        ticketId: serviceRequestId,
      });

      if (response.ok || response.status === 200) {
        const responseData = await response.json();

        toast.success('ⓘ Appointment has been scheduled.', {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          serviceRequest: prev.serviceRequest
            ? { ...prev.serviceRequest, ...responseData.data }
            : null,
        }));

        await fetchServiceRequestActivity(serviceRequestId);
        return;
      }

      throw new Error('Failed to save scheduled appointment');
    } catch (error: any) {
      console.error('Error saving scheduled appointment:', error);
      setError(error.message || 'Failed to save scheduled appointment');

      toast.error(
        '🚫 Something went wrong with adding a service request, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteServiceRequestFromAPI = async (
    serviceRequestId: string
  ): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await deleteServiceRequest(serviceRequestId);

      if (response.ok || response.status === 204) {
        toast.success('ⓘ The service request has been deleted.', {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          serviceRequest: null,
          activity: [],
        }));
        return true;
      }

      throw new Error('Delete request failed');
    } catch (error: any) {
      console.error('Error deleting service request:', error);
      setError(error.message || 'Failed to delete service request');

      toast.error(
        '🚫 Something went wrong with deleting the service request, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateServiceRequestOneYearWarrantyIssueStatusFromAPI = async (
    data: any
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response: any =
        await updateServiceRequestOneYearWarrantyIssueStatus(data);

      if (response.ok || response.status === 200) {
        const responseData: any = await response.json();

        toast.success(
          'ⓘ Service request warranty issue status has been updated.',
          {
            autoClose: 10000,
            position: 'top-center',
          }
        );

        setState((prev: UseServiceRequestAPIState) => ({
          ...prev,
          serviceRequest: prev.serviceRequest
            ? { ...prev.serviceRequest, ...responseData.data }
            : null,
          oneYearWarrantyIssues: responseData.data.isOneYearWarrantyIssue,
        }));
        return;
      }

      throw new Error('Failed to update warranty issue status');
    } catch (error: any) {
      console.error('Error updating warranty issue status:', error);
      setError(error.message || 'Failed to update warranty issue status');

      toast.error(
        '🚫 Something went wrong with changing the status of warranty issue of the service request, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );
    } finally {
      setLoading(false);
    }
  };

  const clearServiceRequestState = () => {
    setState((prev: UseServiceRequestAPIState) => ({
      ...prev,
      serviceRequest: null,
      activity: [],
    }));
  };

  const bulkUpdateStatus = async (payload: any): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await updateBulkStatus(payload);

      if (response.ok || response.status === 200) {
        const responseJson: any = await response.json();

        if (responseJson?.statusCode === 200) {
          toast.success('ⓘ The service requests status has been updated.', {
            autoClose: 5000,
            position: 'top-center',
          });
          return true;
        }

        throw new Error(responseJson?.message || 'Bulk status update failed');
      }

      throw new Error('Bulk status update failed');
    } catch (error: any) {
      console.error('Error updating bulk status:', error);
      setError(error.message || 'Failed to update bulk status');

      toast.error(
        '🚫 Something went wrong with updating service request statuses, please try again.',
        { autoClose: 5000, position: 'top-center' }
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const groupServiceRequests = async (payload: any): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await createServiceRequestByGroup(payload);

      if (response.ok || response.status === 200) {
        const responseJson: any = await response.json();

        if (responseJson?.statusCode === 200) {
          const isAddingToExistingGroup: boolean = Boolean(payload?.id);
          const successMessage: string = isAddingToExistingGroup
            ? 'ⓘ Service requests have been added to the group.'
            : 'ⓘ Service request group has been created.';

          toast.success(successMessage, {
            autoClose: 5000,
            position: 'top-center',
          });
          return true;
        }

        throw new Error(responseJson?.message || 'Grouping failed');
      }

      throw new Error('Grouping failed');
    } catch (error: any) {
      console.error('Error grouping service requests:', error);
      setError(error.message || 'Failed to group service requests');

      toast.error(
        '🚫 Something went wrong with the service request grouping, please try again.',
        { autoClose: 5000, position: 'top-center' }
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeServiceRequestFromGroup = async (
    serviceRequestId: string,
    userId: string
  ): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await deleteServiceRequestFromGroup(
        serviceRequestId,
        userId
      );

      if (response.ok || response.status === 200) {
        const responseJson: any = await response.json();

        if (responseJson?.statusCode === 200) {
          toast.success(
            'ⓘ Service request has been removed from the group.',
            { autoClose: 5000, position: 'top-center' }
          );
          return true;
        }

        throw new Error(
          responseJson?.message || 'Removing from group failed'
        );
      }

      throw new Error('Removing from group failed');
    } catch (error: any) {
      console.error('Error removing service request from group:', error);
      setError(error.message || 'Failed to remove service request from group');

      toast.error(
        '🚫 Something went wrong with removing this service request from the group, please try again.',
        { autoClose: 5000, position: 'top-center' }
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteServiceRequestGroupById = async (
    groupId: string,
    userId: string
  ): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const response: any = await deleteServiceRequestGroup(groupId, userId);

      if (response.ok || response.status === 200) {
        const responseJson: any = await response.json();

        if (responseJson?.statusCode === 200) {
          toast.success('ⓘ Service request group has been deleted.', {
            autoClose: 5000,
            position: 'top-center',
          });
          return true;
        }

        throw new Error(responseJson?.message || 'Group deletion failed');
      }

      throw new Error('Group deletion failed');
    } catch (error: any) {
      console.error('Error deleting service request group:', error);
      setError(error.message || 'Failed to delete service request group');

      toast.error(
        '🚫 Something went wrong with deleting this service request group, please try again.',
        { autoClose: 5000, position: 'top-center' }
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    serviceRequest: state.serviceRequest,
    activity: state.activity,
    loading: state.loading,
    error: state.error,
    oneYearWarrantyIssues: state.oneYearWarrantyIssues,
    fetchServiceRequestById,
    fetchServiceRequestActivity,
    updateStatus,
    saveScheduledAppointmentFromAPI,
    deleteServiceRequestFromAPI,
    updateServiceRequestOneYearWarrantyIssueStatusFromAPI,
    clearServiceRequestState,

    bulkUpdateStatus,
    groupServiceRequests,
    removeServiceRequestFromGroup,
    deleteServiceRequestGroupById,
  };
};
